import classNames from 'classnames';
import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import Playlist from '../../../models/playlist/playlist';
import { LibraryContext } from '../../../services/library/libraryService';
import { buildMatchIndex, MatchMethod } from '../../../utilities/itunes/itunesMatch';
import {
  applyImports,
  doubtDescription,
  formatSeconds,
  importablePlaylists,
  ImportOutcome,
  ImportPlan,
  ImportTarget,
  matchPlaylist,
  missesAsText,
  PlaylistMatchReport,
  PlaylistSummary,
} from '../../../utilities/itunes/itunesImport';
import {
  ItunesLibrary,
  parseItunesLibraryAsync,
  readFileAsText,
} from '../../../utilities/itunes/itunesPlist';
import Dialog from '../dialog/dialog';
import InfoBubble from '../infoBubble/infoBubble';
import { itunesImportInfo } from '../infoBubble/infoTexts';
import s from './importItunes.module.scss';

/**
 * Importing playlists out of an iTunes `Library.xml`.
 *
 * Four steps, and the middle two are the point: pick the playlists, then look
 * at what could and could not be found *before* anything is written. The
 * matching is metadata against metadata across two machines and two
 * filesystems (see `itunesMatch.ts`), so it is right about 99% of the time and
 * the remaining 1% is the user's business, not a number to bury.
 *
 * Rendered into `document.body` like the track editor, above everything.
 *
 * What it does not do is add tracks Fadila has never seen. A playlist entry
 * can only become a Fadila playlist item if the track is already in the
 * library, because a library track *is* a cloud file id and there is no way to
 * invent one from a path on a dead machine. Importing files that were never
 * uploaded is a different feature - docs/ITUNES-IMPORT.md section 6.
 */

interface Props {
  onClose(): void;
}

type Stage =
  | { kind: 'idle' }
  | { kind: 'reading'; fileName: string }
  | { kind: 'parsing'; fileName: string; progress: number }
  | { kind: 'picking' }
  | { kind: 'matching' }
  | { kind: 'review' }
  | { kind: 'importing' }
  | { kind: 'done'; outcomes: ImportOutcome[] }
  | { kind: 'error'; message: string };

/** Rows of a miss list rendered before it turns into "and N more". */
const missListCap = 200;

function percent(part: number, whole: number) {
  if (whole === 0) return '0%';

  return ((100 * part) / whole).toFixed(1).replace(/\.0$/, '') + '%';
}

/** "1 track", "4 tracks" - these counts are read as sentences. */
function plural(count: number, singular: string, plural = singular + 's') {
  return count + ' ' + (count === 1 ? singular : plural);
}

function methodSummary(report: PlaylistMatchReport) {
  const order = [
    MatchMethod.ArtistAlbumTitle,
    MatchMethod.ArtistTitle,
    MatchMethod.FileName,
    MatchMethod.Title,
  ];

  return order
    .filter((method) => report.methodCounts[method])
    .map((method) => report.methodCounts[method] + ' by ' + method)
    .join(', ');
}

export default function ImportItunes({ onClose }: Props) {
  const libCtx = useContext(LibraryContext);

  const [stage, setStage] = useState<Stage>({ kind: 'idle' });
  const [library, setLibrary] = useState<ItunesLibrary>();
  const [summaries, setSummaries] = useState<PlaylistSummary[]>([]);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set<string>());
  const [filter, setFilter] = useState('');
  const [reports, setReports] = useState<PlaylistMatchReport[]>([]);
  const [targets, setTargets] = useState<{ [playlistId: string]: ImportTarget }>({});
  const [includeUncertain, setIncludeUncertain] = useState(false);
  const [includeCopies, setIncludeCopies] = useState(false);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set<string>());
  const [copied, setCopied] = useState<string>();

  // Read in the handlers without making every one of them depend on it.
  const libCtxRef = useRef(libCtx);
  libCtxRef.current = libCtx;

  const busy =
    stage.kind === 'reading' ||
    stage.kind === 'parsing' ||
    stage.kind === 'matching' ||
    stage.kind === 'importing';

  const handleFile = useCallback(async function handleFile(file: File) {
    setStage({ kind: 'reading', fileName: file.name });

    try {
      const xml = await readFileAsText(file);

      setStage({ kind: 'parsing', fileName: file.name, progress: 0 });

      const parsed = await parseItunesLibraryAsync(xml, (progress) => {
        setStage({ kind: 'parsing', fileName: file.name, progress });
      });

      if (parsed.tracks.size === 0) {
        setStage({
          kind: 'error',
          message:
            'No tracks in that file. An iTunes library export is named ' +
            'Library.xml or iTunes Music Library.xml; a single playlist ' +
            'exported from iTunes will not do, it has no track ids in it.',
        });
        return;
      }

      const importable = importablePlaylists(parsed);

      setLibrary(parsed);
      setSummaries(importable);
      setSelected(new Set<string>());
      setStage({ kind: 'picking' });
    } catch (error) {
      setStage({
        kind: 'error',
        message: 'Could not read that file: ' + ((error && error.message) || String(error)),
      });
    }
  }, []);

  const runMatch = useCallback(
    function runMatch() {
      const fadilaLibrary = libCtxRef.current.library;

      if (!library || !fadilaLibrary) {
        return;
      }

      setStage({ kind: 'matching' });

      /*
      Off the click and onto a timeout so React paints "Matching..." first.
      Building the index normalizes about 120,000 strings and takes on the
      order of a second on a 39,000 track library; without the yield the
      button just appears to hang.
    */
      setTimeout(() => {
        try {
          const index = buildMatchIndex(fadilaLibrary);

          const chosen = summaries.filter((summary) => selected.has(summary.playlist.id));
          const matched = chosen.map((summary) => matchPlaylist(index, library, summary));

          const existing = libCtxRef.current.playlists;

          const nextTargets: { [playlistId: string]: ImportTarget } = {};

          for (const report of matched) {
            const name = report.summary.playlist.name;
            // Same name already in Fadila: almost certainly the playlist being
            // rebuilt, so offer to add to it rather than making a second one.
            const sameName = existing.find((pl) => pl.name === name);

            nextTargets[report.summary.playlist.id] = sameName
              ? { mode: 'merge', name, existingId: sameName.id }
              : { mode: 'create', name };
          }

          setReports(matched);
          setTargets(nextTargets);
          setExpanded(new Set<string>());
          setStage({ kind: 'review' });
        } catch (error) {
          setStage({
            kind: 'error',
            message: 'Matching failed: ' + ((error && error.message) || String(error)),
          });
        }
      }, 0);
    },
    [library, summaries, selected],
  );

  const runImport = useCallback(
    function runImport() {
      setStage({ kind: 'importing' });

      setTimeout(async () => {
        try {
          const plans: ImportPlan[] = reports.map((report) => ({
            report,
            target: targets[report.summary.playlist.id],
            includeUncertain,
            includeCopies,
          }));

          const applied = applyImports(libCtxRef.current.playlists, plans);

          libCtxRef.current.setPersistPlaylists(applied.playlists);
          // The write is debounced by a quarter of an hour otherwise, and an
          // import is exactly the moment a reload would be most annoying.
          await libCtxRef.current.savePlaylists();

          setStage({ kind: 'done', outcomes: applied.outcomes });
        } catch (error) {
          setStage({
            kind: 'error',
            message: 'The import failed: ' + ((error && error.message) || String(error)),
          });
        }
      }, 0);
    },
    [reports, targets, includeUncertain, includeCopies],
  );

  function toggleSelected(id: string) {
    const next = new Set(selected);

    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }

    setSelected(next);
  }

  function toggleExpanded(id: string) {
    const next = new Set(expanded);

    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }

    setExpanded(next);
  }

  function setTarget(playlistId: string, target: ImportTarget) {
    setTargets((current) => ({ ...current, [playlistId]: target }));
  }

  async function copyMisses(report: PlaylistMatchReport) {
    const text = missesAsText(report);

    try {
      await navigator.clipboard.writeText(text);
      setCopied(report.summary.playlist.id);
      setTimeout(() => setCopied(undefined), 2000);
    } catch {
      // Clipboard write needs a permission that a file:// or an old Safari
      // will not give. The list is on screen and selectable either way.
      setCopied('failed');
      setTimeout(() => setCopied(undefined), 4000);
    }
  }

  const visibleSummaries = useMemo(() => {
    const needle = filter.trim().toLowerCase();

    if (!needle) return summaries;

    return summaries.filter((summary) => summary.displayName.toLowerCase().indexOf(needle) !== -1);
  }, [summaries, filter]);

  const selectedCount = selected.size;

  const totals = useMemo(
    () =>
      reports.reduce(
        (acc, report) => ({
          matched: acc.matched + report.matched,
          uncertain: acc.uncertain + report.uncertain,
          copies: acc.copies + report.copies,
          ambiguous: acc.ambiguous + report.ambiguous,
          unmatched: acc.unmatched + report.unmatched,
        }),
        { matched: 0, uncertain: 0, copies: 0, ambiguous: 0, unmatched: 0 },
      ),
    [reports],
  );

  /** Everything the import would write, which is what the button acts on. */
  const wouldAdd =
    totals.matched +
    (includeUncertain ? totals.uncertain : 0) +
    (includeCopies ? totals.copies : 0);

  function renderIdle() {
    if (!libCtx.libraryLoaded || !libCtx.library) {
      return (
        <p className={s.help}>
          The library is still loading. Give it a moment and open this again.
        </p>
      );
    }

    return (
      <>
        <p className={s.help}>
          Rebuilds an iTunes playlist here by looking every track up in the library you already
          have. Nothing is downloaded and no files are touched; the only thing written is your
          playlists.
        </p>
        <p className={s.help}>
          In iTunes or the Music app the file is <b>File &rarr; Library &rarr; Export Library</b>,
          which writes a <code>Library.xml</code>. A big one is fine - the reference library for
          this feature is 70 MB and 41,000 tracks - but do it on a computer rather than a phone.
        </p>
        <label className={s.fileButton}>
          <input
            className={s.fileInput}
            type="file"
            accept=".xml,text/xml,application/xml"
            onChange={(ev) => {
              const file = ev.target.files && ev.target.files[0];
              // Clear it so picking the same file again still fires a change.
              ev.target.value = '';

              if (file) handleFile(file);
            }}
          />
          <span className="button">Choose Library.xml</span>
        </label>
      </>
    );
  }

  function renderPicking() {
    return (
      <>
        <p className={s.help}>
          {summaries.length} playlists to choose from.
          {library && library.musicFolder ? (
            <>
              {' '}
              This library lived in <code>{library.musicFolder}</code>.
            </>
          ) : null}{' '}
          iTunes&apos; own lists (Library, Music, Podcasts and the rest) and empty ones are left
          out. A folder of playlists holds every track of everything inside it.
        </p>

        <div className={s.pickerControls}>
          <input
            className={s.filterInput}
            type="search"
            placeholder="Filter playlists"
            value={filter}
            onChange={(ev) => setFilter(ev.target.value)}
          />
          <button
            type="button"
            className="button secondary"
            onClick={() => setSelected(new Set(visibleSummaries.map((x) => x.playlist.id)))}
          >
            Select {filter.trim() ? 'shown' : 'all'}
          </button>
          <button
            type="button"
            className="button secondary"
            onClick={() => setSelected(new Set<string>())}
          >
            Select none
          </button>
        </div>

        <ul className={s.playlistList}>
          {visibleSummaries.map((summary) => (
            <li key={summary.playlist.id} className={s.playlistRow}>
              <label className={s.playlistLabel}>
                <input
                  type="checkbox"
                  className={s.checkbox}
                  checked={selected.has(summary.playlist.id)}
                  onChange={() => toggleSelected(summary.playlist.id)}
                />
                <span className={s.playlistName}>{summary.displayName}</span>
                <span className={s.playlistMeta}>
                  {plural(summary.fileItemCount, 'track')}
                  {summary.itemCount !== summary.fileItemCount
                    ? ' (' +
                      plural(summary.itemCount - summary.fileItemCount, 'stream') +
                      ' skipped)'
                    : ''}
                  {summary.playlist.isFolder ? ' · folder' : ''}
                  {summary.playlist.isSmart ? ' · smart' : ''}
                </span>
              </label>
            </li>
          ))}
        </ul>

        <div className={s.actions}>
          <button type="button" className="button secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="button"
            disabled={selectedCount === 0}
            onClick={runMatch}
          >
            {selectedCount === 0
              ? 'Match tracks'
              : 'Match tracks in ' + selectedCount + ' playlist' + (selectedCount === 1 ? '' : 's')}
          </button>
        </div>
      </>
    );
  }

  function renderTargetPicker(report: PlaylistMatchReport, existing: Playlist[]) {
    const playlistId = report.summary.playlist.id;
    const target = targets[playlistId];

    if (!target) return null;

    return (
      <div className={s.targetRow}>
        <select
          className={s.targetSelect}
          value={target.mode === 'merge' ? 'merge:' + target.existingId : 'create'}
          onChange={(ev) => {
            const value = ev.target.value;

            if (value === 'create') {
              setTarget(playlistId, { mode: 'create', name: target.name });
            } else {
              setTarget(playlistId, {
                mode: 'merge',
                name: target.name,
                existingId: value.slice('merge:'.length),
              });
            }
          }}
        >
          <option value="create">Create a new playlist</option>
          {existing.map((pl) => (
            <option key={pl.id} value={'merge:' + pl.id}>
              Add to &quot;{pl.name}&quot; ({pl.tracks.length})
            </option>
          ))}
        </select>

        {target.mode === 'create' && (
          <input
            className={s.nameInput}
            type="text"
            value={target.name}
            aria-label="New playlist name"
            onChange={(ev) => setTarget(playlistId, { mode: 'create', name: ev.target.value })}
          />
        )}
      </div>
    );
  }

  function renderReview() {
    const existing = libCtx.playlists;

    return (
      <>
        <p className={s.help}>
          {totals.matched} of{' '}
          {totals.matched + totals.uncertain + totals.ambiguous + totals.unmatched} tracks found in
          your library
          {totals.uncertain > 0 ? ' · ' + totals.uncertain + ' uncertain' : ''}
          {totals.ambiguous > 0 ? ' · ' + totals.ambiguous + ' ambiguous' : ''}
          {totals.copies > 0 ? ' (' + totals.copies + ' of them copies you already have)' : ''}
          {totals.unmatched > 0 ? ' · ' + totals.unmatched + ' not found' : ''}. Nothing has been
          written yet.
        </p>

        {reports.map((report) => {
          const playlistId = report.summary.playlist.id;
          const total = report.items.length;
          const misses = report.items.filter((item) => item.result.kind !== 'matched');
          const isExpanded = expanded.has(playlistId);

          return (
            <section key={playlistId} className={s.reportCard}>
              <h6 className={s.reportHeading}>{report.summary.displayName}</h6>

              <p className={s.reportCounts}>
                <span className={s.good}>{report.matched} found</span> (
                {percent(report.matched, total)})
                {report.uncertain > 0 && (
                  <span className={s.warn}> &middot; {report.uncertain} uncertain</span>
                )}
                {report.ambiguous > 0 && (
                  <span className={s.warn}>
                    {' '}
                    &middot; {report.ambiguous} ambiguous
                    {report.copies > 0 ? ' (' + report.copies + ' copies)' : ''}
                  </span>
                )}
                {report.unmatched > 0 && (
                  <span className={s.bad}> &middot; {report.unmatched} not found</span>
                )}
                {report.streams > 0 && (
                  <span className={s.muted}>
                    {' '}
                    &middot; {plural(report.streams, 'stream')} skipped
                  </span>
                )}
                {report.danglingIds > 0 && (
                  <span className={s.muted}> &middot; {report.danglingIds} not in the file</span>
                )}
              </p>

              {report.matched > 0 && <p className={s.reportMethods}>{methodSummary(report)}</p>}

              {renderTargetPicker(report, existing)}

              {misses.length > 0 && (
                <>
                  <div className={s.missControls}>
                    <button
                      type="button"
                      className={s.linkButton}
                      onClick={() => toggleExpanded(playlistId)}
                    >
                      {isExpanded ? 'Hide' : 'Show'} the {misses.length} it could not place for
                      certain
                    </button>
                    <button
                      type="button"
                      className={s.linkButton}
                      onClick={() => copyMisses(report)}
                    >
                      {copied === playlistId
                        ? 'Copied'
                        : copied === 'failed'
                          ? 'Could not copy'
                          : 'Copy as text'}
                    </button>
                  </div>

                  {isExpanded && (
                    <ul className={s.missList}>
                      {misses.slice(0, missListCap).map((item, i) => (
                        <li key={i} className={s.missRow}>
                          <div className={s.missMain}>
                            <span
                              className={classNames(
                                s.missKind,
                                item.result.kind === 'unmatched' ? s.bad : s.warn,
                              )}
                            >
                              {item.result.kind === 'unmatched'
                                ? 'not found'
                                : item.result.kind === 'ambiguous' && item.result.sameRecording
                                  ? 'copies'
                                  : item.result.kind}
                            </span>
                            <span className={s.missTitle}>
                              {item.itunesTrack.artist
                                ? item.itunesTrack.artist + ' - ' + item.itunesTrack.name
                                : item.itunesTrack.name}
                            </span>
                            <span className={s.missPath}>{item.itunesTrack.location}</span>
                          </div>
                          {/*
                            Why this row is not a match, on its own line.
                            The two distances on an uncertain row are the
                            whole basis for deciding, so they cannot be the
                            part that gets truncated - which is what happened
                            when they shared the line with the title.
                          */}
                          {item.result.kind !== 'unmatched' && (
                            <div className={s.missDetail}>
                              <span className={s.missReason}>{doubtDescription(item.result)}</span>
                              {item.result.kind === 'uncertain' && (
                                <span className={s.missOffered}>
                                  {' · would add '}
                                  {item.result.track.title}
                                  {item.result.track.album ? ' on ' + item.result.track.album : ''}
                                  {', '}
                                  {formatSeconds(item.result.track.length)}
                                </span>
                              )}
                            </div>
                          )}
                        </li>
                      ))}
                      {misses.length > missListCap && (
                        <li className={s.missRow}>
                          <span className={s.muted}>
                            and {misses.length - missListCap} more - use Copy as text
                          </span>
                        </li>
                      )}
                    </ul>
                  )}
                </>
              )}
            </section>
          );
        })}

        {/*
          The two opt-ins on this screen, each deliberately narrow. Copies:
          the library holds one recording several times and any is the right
          music, so the only thing being chosen is which file. Uncertain: one
          candidate is close on length and nothing else is. An ambiguous row
          that is neither has no candidate that stands out, so nothing here
          can offer one - those are reported and left alone, always.
        */}
        {(totals.copies > 0 || totals.uncertain > 0) && (
          <div className={s.ambiguousBlock}>
            {totals.copies > 0 && (
              <>
                <label className={s.ambiguousToggle}>
                  <input
                    type="checkbox"
                    className={s.checkbox}
                    checked={includeCopies}
                    onChange={(ev) => setIncludeCopies(ev.target.checked)}
                  />
                  <span>
                    Also add one copy for{' '}
                    {totals.copies === 1
                      ? 'the 1 track'
                      : 'each of the ' + totals.copies + ' tracks'}{' '}
                    your library holds more than once
                  </span>
                </label>
                <p className={s.ambiguousHelp}>
                  These are marked <b>copies</b> in the list: the same recording is in your library
                  several times - the same release uploaded twice, a rip and its rerip, an mp3 and
                  an m4a - with lengths within a twentieth of a second of each other. Any of them is
                  the right music, so no guessing about what plays is involved. Each entry gets the
                  copy closest in length, and two entries that land on the same copy add it once.
                </p>
              </>
            )}
            {totals.uncertain > 0 && (
              <>
                <label className={s.ambiguousToggle}>
                  <input
                    type="checkbox"
                    className={s.checkbox}
                    checked={includeUncertain}
                    onChange={(ev) => setIncludeUncertain(ev.target.checked)}
                  />
                  <span>
                    Also add the{' '}
                    {totals.uncertain === 1
                      ? '1 uncertain match'
                      : totals.uncertain + ' uncertain matches'}
                  </span>
                </label>
                <p className={s.ambiguousHelp}>
                  An <b>uncertain</b> row is one where the artist, album and title match exactly but
                  no track in your library has the right length: the closest is seconds out while
                  the alternatives are far off. That is usually a different rip or a trimmed copy of
                  the same cut, and sometimes the wrong track altogether. Each row says how far out
                  it is and what it would add, so you can judge them yourself - open the list above.
                  Left off, they are reported and not imported.
                </p>
              </>
            )}
            <p className={s.ambiguousHelp}>
              Any other <b>ambiguous</b> row is never imported. There, several tracks in your
              library share the artist, album and title and their lengths are different enough to be
              different cuts yet too close to tell which is meant, so there is no candidate to
              offer. Those are yours to sort out by hand.
            </p>
          </div>
        )}

        <div className={s.actions}>
          <button
            type="button"
            className="button secondary"
            onClick={() => setStage({ kind: 'picking' })}
          >
            Back
          </button>
          <button type="submit" className="button" disabled={wouldAdd === 0} onClick={runImport}>
            Import
          </button>
        </div>
      </>
    );
  }

  function renderDone(outcomes: ImportOutcome[]) {
    return (
      <>
        <p className={s.help}>Done, and saved to your storage provider.</p>
        <ul className={s.outcomeList}>
          {outcomes.map((outcome) => (
            <li key={outcome.playlistId}>
              <b>{outcome.targetName}</b>
              {outcome.created ? ' created with ' : ' now holds '}
              {outcome.added} added {outcome.added === 1 ? 'track' : 'tracks'}
              {outcome.duplicates > 0 ? ' (' + outcome.duplicates + ' were already in it)' : ''}
            </li>
          ))}
        </ul>
        <div className={s.actions}>
          <button type="submit" className="button" onClick={onClose}>
            Close
          </button>
        </div>
      </>
    );
  }

  function renderBody() {
    switch (stage.kind) {
      case 'idle':
        return renderIdle();

      case 'reading':
        return <p className={s.help}>Reading {stage.fileName}...</p>;

      case 'parsing':
        return (
          <>
            <p className={s.help}>Reading the library: {Math.round(stage.progress * 100)}%</p>
            <div className={s.progressTrack}>
              <div
                className={s.progressBar}
                style={{ width: Math.round(stage.progress * 100) + '%' }}
              />
            </div>
          </>
        );

      case 'picking':
        return renderPicking();

      case 'matching':
        return (
          <p className={s.help}>
            Looking every track up in your library. On a big library this takes a few seconds.
          </p>
        );

      case 'review':
        return renderReview();

      case 'importing':
        return <p className={s.help}>Writing the playlists...</p>;

      case 'done':
        return renderDone(stage.outcomes);

      case 'error':
        return (
          <>
            <p className={classNames(s.help, s.bad)}>{stage.message}</p>
            <div className={s.actions}>
              <button
                type="button"
                className="button secondary"
                onClick={() => setStage({ kind: 'idle' })}
              >
                Try another file
              </button>
              <button type="submit" className="button" onClick={onClose}>
                Close
              </button>
            </div>
          </>
        );

      default:
        return null;
    }
  }

  return (
    <Dialog
      titleId="importItunesTitle"
      title={
        <>
          Import iTunes playlists
          <InfoBubble label={itunesImportInfo.label}>{itunesImportInfo.text}</InfoBubble>
        </>
      }
      onClose={onClose}
      // Not while something is running: the parse and the import both mutate
      // state on completion and there would be nothing left to mutate.
      closable={!busy}
      size="large"
      // A form, so that Enter runs the step's primary button (the one submit).
      onSubmit={(ev) => ev.preventDefault()}
    >
      {renderBody()}
    </Dialog>
  );
}
